import { createReducer, on } from '@ngrx/store';
import {
  BulkFirmwareUpdateResponse,
  BulkStartFirmwareUpdateResponse,
  FirmwareModel,
} from '@core/models';
import { FirmwareActions } from '../actions/firmware.actions';

export interface FirmwareState {
  isLoading: boolean;
  isUpdatingFirmware: boolean;
  firmwareUpdateSuccess: boolean | undefined;
  firmwareItems: FirmwareModel[];
  error: unknown;
  isBulkUpdating: boolean;
  bulkUpdateProgress:
    | { total: number; inProgress: number; succeeded: number; failed: number }
    | undefined;
  bulkUpdateResponse: BulkFirmwareUpdateResponse | undefined;
  bulkDlmsUpdateResponse: BulkStartFirmwareUpdateResponse | undefined;
}

export const initialFirmwareState: FirmwareState = {
  isLoading: false,
  isUpdatingFirmware: false,
  firmwareUpdateSuccess: undefined,
  firmwareItems: [],
  error: null,
  isBulkUpdating: false,
  bulkUpdateProgress: undefined,
  bulkUpdateResponse: undefined,
  bulkDlmsUpdateResponse: undefined,
};

export const firmwareReducer = createReducer(
  initialFirmwareState,

  // Get Firmware Versions
  on(FirmwareActions.getFirmwareVersions, state => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(FirmwareActions.getFirmwareVersionsSuccess, (state, { items }) => ({
    ...state,
    firmwareItems: items,
    isLoading: false,
  })),
  on(FirmwareActions.getFirmwareVersionsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Deactivate Firmware
  on(FirmwareActions.deactivateFirmware, state => ({
    ...state,
    error: null,
  })),
  on(FirmwareActions.deactivateFirmwareSuccess, (state, { firmwareId }) => ({
    ...state,
    firmwareItems: state.firmwareItems.map(fw =>
      fw.firmwareId === firmwareId ? { ...fw, isActive: false } : fw,
    ),
  })),
  on(FirmwareActions.deactivateFirmwareFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Update Device Firmware (EnnyLCU and DLMS share the update-state flags)
  on(FirmwareActions.updateDeviceFirmware, FirmwareActions.startDlmsFirmwareUpdate, state => ({
    ...state,
    isUpdatingFirmware: true,
    firmwareUpdateSuccess: undefined,
    error: null,
  })),
  on(
    FirmwareActions.updateDeviceFirmwareSuccess,
    FirmwareActions.startDlmsFirmwareUpdateSuccess,
    state => ({
      ...state,
      isUpdatingFirmware: false,
      firmwareUpdateSuccess: true,
    }),
  ),
  on(
    FirmwareActions.updateDeviceFirmwareFailure,
    FirmwareActions.startDlmsFirmwareUpdateFailure,
    (state, { error }) => ({
      ...state,
      isUpdatingFirmware: false,
      firmwareUpdateSuccess: false,
      error,
    }),
  ),

  // Reset Update State
  on(FirmwareActions.resetUpdateState, state => ({
    ...state,
    isUpdatingFirmware: false,
    firmwareUpdateSuccess: undefined,
    error: null,
  })),

  // Bulk Update Device Firmware (EnnyLCU)
  on(FirmwareActions.bulkUpdateDeviceFirmware, (state, { request }) => ({
    ...state,
    isBulkUpdating: true,
    bulkUpdateProgress: {
      total: request.device_ids.length,
      inProgress: 0,
      succeeded: 0,
      failed: 0,
    },
    error: null,
  })),
  on(FirmwareActions.bulkUpdateDeviceFirmwareSuccess, (state, { response }) => ({
    ...state,
    isBulkUpdating: false,
    bulkUpdateResponse: response,
  })),
  on(FirmwareActions.bulkUpdateDeviceFirmwareFailure, (state, { error }) => ({
    ...state,
    isBulkUpdating: false,
    error,
  })),

  // Bulk Start Firmware Update (DLMS)
  on(FirmwareActions.bulkStartDlmsFirmwareUpdate, (state, { request }) => ({
    ...state,
    isBulkUpdating: true,
    bulkDlmsUpdateResponse: undefined,
    bulkUpdateProgress: {
      total: request.deviceIds.length,
      inProgress: request.deviceIds.length,
      succeeded: 0,
      failed: 0,
    },
    error: null,
  })),
  on(FirmwareActions.bulkStartDlmsFirmwareUpdateSuccess, (state, { response }) => ({
    ...state,
    isBulkUpdating: false,
    bulkDlmsUpdateResponse: response,
    bulkUpdateProgress: {
      total: response.accepted.length + response.rejected.length,
      inProgress: 0,
      succeeded: response.accepted.length,
      failed: response.rejected.length,
    },
  })),
  on(FirmwareActions.bulkStartDlmsFirmwareUpdateFailure, (state, { error }) => ({
    ...state,
    isBulkUpdating: false,
    error,
  })),

  // Update Bulk Progress
  on(FirmwareActions.updateBulkProgress, (state, { status }) => {
    if (!state.bulkUpdateProgress) {
      return state;
    }
    const progress = { ...state.bulkUpdateProgress };
    if (status === 'in_progress') {
      progress.inProgress = progress.inProgress + 1;
    } else if (status === 'success') {
      progress.inProgress = Math.max(0, progress.inProgress - 1);
      progress.succeeded = progress.succeeded + 1;
    } else if (status === 'failed') {
      progress.inProgress = Math.max(0, progress.inProgress - 1);
      progress.failed = progress.failed + 1;
    }
    return { ...state, bulkUpdateProgress: progress };
  }),

  // Reset Bulk Update State
  on(FirmwareActions.resetBulkUpdateState, state => ({
    ...state,
    isBulkUpdating: false,
    bulkUpdateProgress: undefined,
    bulkUpdateResponse: undefined,
    bulkDlmsUpdateResponse: undefined,
    error: null,
  })),
);
